<template>
  <div style="margin: 20px">
    <div style="color: #333; font-size: 20px; font-weight: bold; margin-bottom: 20px">
      Markdown 字数统计
    </div>

    <!-- Search Form -->
    <el-form :inline="true" size="small" @submit.native.prevent="fetchStats">
      <el-form-item label="账号">
        <el-input v-model="keyword" placeholder="输入邮箱或用户名" style="width: 260px" clearable></el-input>
      </el-form-item>
      <el-form-item label="日期范围">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          style="width: 300px"
          @change="activeQuick = ''"
        ></el-date-picker>
      </el-form-item>
      <el-form-item>
        <el-button-group>
          <el-button size="small" :type="activeQuick === 'thisMonth' ? 'primary' : ''" @click="setThisMonth">本月</el-button>
          <el-button size="small" :type="activeQuick === 'lastMonth' ? 'primary' : ''" @click="setLastMonth">上月</el-button>
        </el-button-group>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="fetchStats" :loading="loading">查询</el-button>
      </el-form-item>
    </el-form>

    <!-- User Info -->
    <div v-if="stats && stats.user_name" style="margin-bottom: 16px; color: #606266; font-size: 14px">
      用户：{{ stats.user_name }}（{{ stats.user_email }}）
    </div>

    <!-- Summary Cards -->
    <el-row :gutter="16" style="margin-bottom: 20px" v-if="stats">
      <el-col :span="6">
        <el-card shadow="hover" :body-style="{ padding: '16px' }">
          <div style="color: #909399; font-size: 13px; margin-bottom: 6px">总字数</div>
          <div style="font-size: 28px; font-weight: bold; color: #409EFF">{{ stats.total_word_count.toLocaleString() }}</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" :body-style="{ padding: '16px' }">
          <div style="color: #909399; font-size: 13px; margin-bottom: 6px">文档数</div>
          <div style="font-size: 28px; font-weight: bold; color: #67C23A">{{ stats.total_documents }}</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" :body-style="{ padding: '16px' }">
          <div style="color: #909399; font-size: 13px; margin-bottom: 6px">平均字数/篇</div>
          <div style="font-size: 28px; font-weight: bold; color: #E6A23C">
            {{ stats.total_documents > 0 ? Math.round(stats.total_word_count / stats.total_documents).toLocaleString() : 0 }}
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" :body-style="{ padding: '16px' }">
          <div style="color: #909399; font-size: 13px; margin-bottom: 6px">活跃天数</div>
          <div style="font-size: 28px; font-weight: bold; color: #F56C6C">{{ stats.daily_stats.length }}</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Daily Stats Table -->
    <div v-if="stats && stats.daily_stats.length > 0" style="margin-bottom: 20px">
      <div style="font-size: 16px; font-weight: bold; color: #333; margin-bottom: 12px">每日统计</div>
      <el-table :data="stats.daily_stats" style="width: 100%" size="small" show-summary :summary-method="dailySummary">
        <el-table-column prop="date" label="日期" width="160" sortable></el-table-column>
        <el-table-column prop="word_count" label="字数" width="160" sortable>
          <template slot-scope="scope">
            {{ scope.row.word_count.toLocaleString() }}
          </template>
        </el-table-column>
        <el-table-column label="占比" width="200">
          <template slot-scope="scope">
            <el-progress
              :percentage="stats.total_word_count > 0 ? Math.round(scope.row.word_count / stats.total_word_count * 100) : 0"
              :stroke-width="14"
              :text-inside="true"
            ></el-progress>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Documents Detail Table -->
    <div v-if="stats && stats.documents.length > 0">
      <div style="font-size: 16px; font-weight: bold; color: #333; margin-bottom: 12px">文档明细</div>
      <el-table :data="stats.documents" style="width: 100%" size="small" show-summary :summary-method="docSummary">
        <el-table-column type="index" label="#" width="50"></el-table-column>
        <el-table-column prop="file_name" label="文件名" min-width="240" show-overflow-tooltip></el-table-column>
        <el-table-column prop="word_count" label="字数" width="120" sortable>
          <template slot-scope="scope">
            {{ scope.row.word_count.toLocaleString() }}
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="180" sortable prop="created_at">
          <template slot-scope="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="180" sortable prop="updated_at">
          <template slot-scope="scope">
            {{ formatDate(scope.row.updated_at) }}
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Empty state -->
    <el-empty v-if="searched && (!stats || stats.documents.length === 0)" description="未找到该用户的文档数据"></el-empty>
  </div>
</template>

<script>
import { postRequest } from '@/apis/lfAdmin'

export default {
  name: 'markdownStats',
  data() {
    return {
      keyword: '',
      dateRange: null,
      activeQuick: 'thisMonth',
      loading: false,
      searched: false,
      stats: null
    }
  },
  created() {
    this.setThisMonth()
  },
  methods: {
    formatYMD(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    setThisMonth() {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      this.dateRange = [this.formatYMD(start), this.formatYMD(end)]
      this.activeQuick = 'thisMonth'
    },
    setLastMonth() {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      this.dateRange = [this.formatYMD(start), this.formatYMD(end)]
      this.activeQuick = 'lastMonth'
    },
    async fetchStats() {
      if (!this.keyword) {
        this.$message.warning('请输入邮箱或用户名')
        return
      }
      this.loading = true
      this.searched = true
      try {
        const params = { keyword: this.keyword }
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_date = this.dateRange[0]
          params.end_date = this.dateRange[1]
        }
        const res = await postRequest('getMarkdownWordCountStats', params)
        if (res.data && res.data.code >= 0) {
          this.stats = res.data.data
        } else {
          this.$message.error(res.data.msg || '查询失败')
          this.stats = null
        }
      } catch (e) {
        this.$message.error('请求失败')
        this.stats = null
      } finally {
        this.loading = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    },
    dailySummary({ columns, data }) {
      const sums = []
      columns.forEach((col, index) => {
        if (index === 0) {
          sums[index] = '合计'
        } else if (col.property === 'word_count') {
          sums[index] = data.reduce((sum, row) => sum + row.word_count, 0).toLocaleString()
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
    docSummary({ columns, data }) {
      const sums = []
      columns.forEach((col, index) => {
        if (index === 0) {
          sums[index] = '合计'
        } else if (col.property === 'word_count') {
          sums[index] = data.reduce((sum, row) => sum + row.word_count, 0).toLocaleString()
        } else {
          sums[index] = ''
        }
      })
      return sums
    }
  }
}
</script>
